import type { ConciseBranch } from '@festina/configuration-general-types/configuration'
import { ErrorMessage, Yup } from '@festina-life/components'
import { type Optional, type SetOptional, typeUtils } from '@festina-life/core'
import { toast } from '@festina-life/ui/feedback'
import { FormikForm, FormikSelect } from '@festina-life/ui/formikForms'
import { useTranslate } from '@festina-life/ui/hooks'
import { Stack, useClose } from '@festina-life/ui/structure'
import { useState } from 'react'

import { useAllConciseBranches } from '../../../../uiModels/branches/useAllConciseBranches'
import { useRequiredWorkspaceOperationsContext } from '../../../../workspace/operationsContext'
import { useWorkspaceManagerContext } from '../../../../workspace/workspaceManagerContext'
import { OperationsLoadedButton } from '../../../operationsButton/OperationsLoadedButton'
import { useInvalidateWorkspaceAfterHistoryRewrite } from './useInvalidateWorkspaceAfterHistoryRewrite'

type ChangeWorkspaceBranchInput = {
  branch?: Optional<SetOptional<ConciseBranch, '_type'>>
}

const validate = typeUtils.createValidator<ChangeWorkspaceBranchInput>()

export const ChangeWorkspaceBranchForm = () => {
  const translate = useTranslate()
  const [error, setError] = useState<Optional<Error>>()
  const [isPending, setIsPending] = useState(false)
  const close = useClose()
  const { workspaceKey } = useRequiredWorkspaceOperationsContext()
  const { onUpdate } = useWorkspaceManagerContext()
  const allConciseBranches = useAllConciseBranches()
  const invalidateWorkspaceAfterHistoryRewrite =
    useInvalidateWorkspaceAfterHistoryRewrite()

  const serverBranches = allConciseBranches[workspaceKey.branchKey.server] ?? []

  const currentBranch = serverBranches.find(
    ({ name }) => name === workspaceKey.branchKey.name,
  )

  const handleSubmit = async ({ branch }: ChangeWorkspaceBranchInput) => {
    if (!branch?.name || branch.name === workspaceKey.branchKey.name) {
      return
    }

    setError(undefined)
    setIsPending(true)

    const nextWorkspaceKey = {
      name: workspaceKey.name,
      branchKey: {
        server: workspaceKey.branchKey.server,
        name: branch.name,
      },
    }

    try {
      await onUpdate(nextWorkspaceKey, true)
      void invalidateWorkspaceAfterHistoryRewrite(nextWorkspaceKey)

      toast({
        variant: 'success',
        title: translate('workspaceDetailsPanel.toast.branchChanged', {
          workspaceName: workspaceKey.name,
          branchName: branch.name,
        }),
      })
      close()
    } catch (error) {
      setError(error)
    } finally {
      setIsPending(false)
    }
  }

  const validationSchema = Yup.object({
    branch: Yup.object().required(translate('validation.required')),
  }) satisfies Yup.Schema<ChangeWorkspaceBranchInput>

  return (
    <FormikForm
      initialValues={{ branch: currentBranch ?? undefined }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      <FormikSelect
        options={serverBranches}
        name={validate('branch')}
        label={translate(
          'workspaceDetailsPanel.tabs.manage.section.update.changeBranch.label',
        )}
        renderFn={(branch) => branch.name ?? ''}
        keyFn={(branch) => branch.name ?? ''}
      />

      {error && <ErrorMessage error={error} />}

      <Stack className="w-full justify-end">
        <OperationsLoadedButton
          type="submit"
          variant="primary"
          isLoading={isPending}
        >
          {translate('workspaceDetailsPanel.button.changeBranch')}
        </OperationsLoadedButton>
      </Stack>
    </FormikForm>
  )
}
